import { PageShell } from '@/components/ui/PageShell';
import { Card, SectionHeader } from '@/components/ui';
import { useSettingsStore } from '@/store';
import { Volume2, Zap, CloudOff, Database, Info, RotateCcw, RefreshCw, User, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOnboardingStore } from '@/store/onboardingStore';
import { useAuthStore } from '@/store/authStore';
import { audioEngine } from '@/lib/audio/AudioEngine';
import { useNavigate } from 'react-router-dom';

const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '?';

export function SettingsPage() {
    const { audioMode, setAudioMode } = useSettingsStore();
    const resetOnboarding = useOnboardingStore((s) => s.reset);
    const logout = useAuthStore(s => s.logout);
    const navigate = useNavigate();

    return (
        <PageShell title="Settings" subtitle="Configure your experience">
            <div className="px-4 space-y-6 pb-4 pt-2">
                <div className="space-y-3 animate-fade-in-up stagger-1">
                    <SectionHeader title="Audio & Haptics" />

                    {/* Audio Debug Card */}
                    <Card className="mb-4 p-5 bg-bg-800 border-white/[0.05]">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gold-500/10 flex items-center justify-center text-gold-500">
                                    <Volume2 className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-text-primary">Sound Check</h3>
                                    <p className="text-[11px] text-text-muted mt-0.5">Test your device audio</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    audioEngine.resume();
                                    audioEngine.playCue('ui_success');
                                    alert(`Audio State: ${audioEngine.state}`);
                                }}
                                className="px-4 py-2 bg-gold-500 text-bg-900 text-xs font-bold uppercase tracking-wider rounded-lg shadow-lg active:scale-95 transition"
                            >
                                Play Test
                            </button>
                        </div>
                        <div className="text-[10px] text-text-muted font-mono bg-black/20 p-2 rounded">
                            Status: <span id="audio-status">Check on Play...</span>
                        </div>
                    </Card>

                    <Card className="space-y-px overflow-hidden p-0 bg-transparent border-white/[0.03]">
                        <div
                            onClick={() => setAudioMode('on')}
                            className={cn(
                                "p-5 border-b border-white/[0.05] group cursor-pointer transition-colors",
                                audioMode === 'on' ? 'bg-bg-800' : 'bg-bg-800/60 hover:bg-bg-700'
                            )}
                        >
                            <div className="flex items-center gap-4">
                                <div className={cn(
                                    "w-10 h-10 rounded-xl flex items-center justify-center transition-transform shadow-lg",
                                    audioMode === 'on'
                                        ? "bg-gold-500/10 text-gold-500 scale-100 shadow-gold-500/5"
                                        : "bg-white/[0.03] text-text-muted scale-95 shadow-none group-hover:scale-100"
                                )}>
                                    <Volume2 className="w-5 h-5 fill-current" />
                                </div>
                                <div className="flex-1">
                                    <h3 className={audioMode === 'on' ? "text-text-primary" : "text-text-secondary"}>Sound On</h3>
                                    <p className="text-[11px] text-text-muted mt-0.5">Full audio feedback and whistle cues</p>
                                </div>
                                {audioMode === 'on' && (
                                    <div className="w-2 h-2 rounded-full bg-gold-500 shadow-[0_0_8px_rgba(212,175,55,0.6)] animate-fade-in" />
                                )}
                            </div>
                        </div>
                        <div
                            onClick={() => setAudioMode('haptics-only')}
                            className={cn(
                                "p-5 border-b border-white/[0.05] flex items-center gap-4 group cursor-pointer transition-colors",
                                audioMode === 'haptics-only' ? 'bg-bg-800' : 'bg-bg-800/60 hover:bg-bg-700'
                            )}
                        >
                            <Zap className={cn("w-5 h-5 ml-2.5 transition-colors", audioMode === 'haptics-only' ? "text-gold-500" : "text-text-muted")} />
                            <div className="flex-1">
                                <h3 className={audioMode === 'haptics-only' ? "text-text-primary" : "text-text-secondary"}>Haptics Only</h3>
                                <p className="text-[11px] text-text-muted mt-0.5">Vibration only</p>
                            </div>
                            {audioMode === 'haptics-only' && (
                                <div className="w-2 h-2 rounded-full bg-gold-500 shadow-[0_0_8px_rgba(212,175,55,0.6)] animate-fade-in" />
                            )}
                        </div>
                        <div
                            onClick={() => setAudioMode('off')}
                            className={cn(
                                "p-5 flex items-center gap-4 group cursor-pointer transition-colors",
                                audioMode === 'off' ? 'bg-bg-800' : 'bg-bg-800/60 hover:bg-bg-700'
                            )}
                        >
                            <CloudOff className={cn("w-5 h-5 ml-2.5 transition-colors", audioMode === 'off' ? "text-text-primary" : "text-text-muted")} />
                            <div className="flex-1">
                                <h3 className={audioMode === 'off' ? "text-text-primary" : "text-text-secondary"}>Silent Mode</h3>
                            </div>
                            {audioMode === 'off' && (
                                <div className="w-2 h-2 rounded-full bg-text-muted shadow-[0_0_8px_rgba(255,255,255,0.2)] animate-fade-in" />
                            )}
                        </div>
                    </Card>
                </div>

                {/* Network */}
                <div className="space-y-3 animate-fade-in-up stagger-2">
                    <SectionHeader title="System" />
                    <Card className="p-5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-white/[0.03] flex items-center justify-center text-text-muted">
                                <CloudOff className="w-5 h-5" />
                            </div>
                            <h3 className="text-text-primary">Offline Mode</h3>
                        </div>
                        <div className="flex bg-bg-900 rounded-lg p-1 border border-white/5">
                            <button className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-500 bg-bg-700 rounded-md shadow-sm">Auto</button>
                            <button className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-text-muted">Force</button>
                        </div>
                    </Card>

                    <Card className="p-5 flex items-center justify-between bg-gold-500/5 border-gold-500/20">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-gold-500/10 flex items-center justify-center text-gold-500">
                                <RefreshCw className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-text-primary">Update Check</h3>
                                <p className="text-[11px] text-text-muted">Force clear app cache</p>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                if ('serviceWorker' in navigator) {
                                    navigator.serviceWorker.getRegistrations().then(registrations => {
                                        for (let registration of registrations) {
                                            registration.unregister();
                                        }
                                        window.location.reload();
                                    });
                                } else {
                                    window.location.reload();
                                }
                            }}
                            className="px-4 py-2 bg-gold-500 text-bg-900 text-xs font-bold uppercase tracking-wider rounded-lg shadow-lg active:scale-95 transition"
                        >
                            Update
                        </button>
                    </Card>
                </div>

                {/* Data */}
                <div className="space-y-3 animate-fade-in-up stagger-3">
                    <SectionHeader title="Data Management" />
                    <Card className="space-y-0 p-0 divide-y divide-white/[0.05] overflow-hidden">
                        <button className="w-full flex items-center gap-4 p-5 hover:bg-white/[0.02] transition-colors text-left group">
                            <Database className="w-5 h-5 text-text-muted group-hover:text-gold-500" />
                            <div className="flex-1">
                                <h3 className="text-text-primary group-hover:text-white transition-colors">Export All Data</h3>
                                <p className="text-[11px] text-text-muted mt-0.5">Download as JSON backup</p>
                            </div>
                        </button>
                        <button className="w-full flex items-center gap-4 p-5 hover:bg-red-500/5 transition-colors text-left group">
                            <RotateCcw className="w-5 h-5 text-red-500/50 group-hover:text-red-500" />
                            <div className="flex-1">
                                <h3 className="text-red-500/80 group-hover:text-red-500 transition-colors">Reset & Reseed</h3>
                                <p className="text-[11px] text-red-500/30 mt-0.5">Clear all data and reload defaults</p>
                            </div>
                        </button>
                        <button
                            onClick={async () => {
                                const { seedYouthData } = await import('@/lib/db/seed_youth_data');
                                await seedYouthData();
                                alert('Youth profiles seeded! Go to "Switch Profile" to see them.');
                            }}
                            className="w-full flex items-center gap-4 p-5 hover:bg-gold-500/5 transition-colors text-left group"
                        >
                            <User className="w-5 h-5 text-gold-500/50 group-hover:text-gold-500" />
                            <div className="flex-1">
                                <h3 className="text-gold-500/80 group-hover:text-gold-500 transition-colors">Seed Youth Data</h3>
                                <p className="text-[11px] text-gold-500/30 mt-0.5">Create Greyson, Danika, Gabe profiles</p>
                            </div>
                        </button>
                    </Card>
                </div>

                {/* Training Profile */}
                <div className="space-y-3 animate-fade-in-up stagger-4">
                    <SectionHeader title="Training Profile" />
                    <Card className="p-5 bg-bg-800 border-white/[0.05]">
                        <button
                            onClick={resetOnboarding}
                            className="w-full flex items-center gap-3 text-left"
                        >
                            <div className="w-10 h-10 rounded-xl bg-gold-500/10 flex items-center justify-center text-gold-500">
                                <RefreshCw className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-text-primary font-semibold">Re-do Onboarding</h3>
                                <p className="text-[11px] text-text-muted mt-0.5">Update your goals and experience</p>
                            </div>
                        </button>
                    </Card>
                </div>

                {/* Account */}
                <div className="space-y-3 animate-fade-in-up stagger-5">
                    <SectionHeader title="Account" />
                    <Card className="p-0 overflow-hidden divide-y divide-white/[0.05]">
                        <button
                            onClick={() => navigate('/profiles')}
                            className="w-full flex items-center gap-4 p-5 hover:bg-white/[0.02] transition-colors text-left group"
                        >
                            <User className="w-5 h-5 text-text-muted group-hover:text-gold-500" />
                            <div className="flex-1">
                                <h3 className="text-text-primary group-hover:text-white transition-colors">Switch Profile</h3>
                                <p className="text-[11px] text-text-muted mt-0.5">Switch between athlete profiles</p>
                            </div>
                        </button>
                        <button
                            onClick={logout}
                            className="w-full flex items-center gap-4 p-5 hover:bg-red-500/5 transition-colors text-left group"
                        >
                            <LogOut className="w-5 h-5 text-red-500/50 group-hover:text-red-500" />
                            <div className="flex-1">
                                <h3 className="text-red-500/80 group-hover:text-red-500 transition-colors">Logout</h3>
                                <p className="text-[11px] text-red-500/30 mt-0.5">Sign out of your account</p>
                            </div>
                        </button>
                    </Card>
                </div>

                {/* Footer Info */}
                <div className="pt-4 flex flex-col items-center animate-fade-in-up stagger-5">
                    <div className="w-12 h-12 rounded-2xl bg-bg-800 border border-white/[0.05] flex items-center justify-center mb-3">
                        <Info className="w-6 h-6 text-gold-500/40" />
                    </div>
                    <p className="text-sm font-bold text-text-primary uppercase tracking-[0.2em]">Palaestra Academy</p>
                    <p className="text-[10px] text-text-muted font-medium mt-1 uppercase tracking-widest">v{APP_VERSION} · Elite Systems Engine</p>
                </div>
            </div>
        </PageShell>
    );
}


